import { useState, useEffect } from 'react';

/**
 * Screen shape detection hook for editorial slideshow layouts.
 * 
 * Returns one of three shapes based on viewport size and aspect ratio:
 * - 'big-wide': Large viewport, width >> height (landscape desktop)
 * - 'big-tall': Large viewport, height >= width (portrait/tall desktop)
 * - 'small': Small viewport (mobile)
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.breakpoint - Minimum width to be considered "big" (default: 768)
 * @param {number} options.wideRatio - Width/height ratio threshold for "wide" (default: 1.2)
 * @returns {'big-wide' | 'big-tall' | 'small'} Current screen shape
 */
export function useScreenShape(options = {}) {
    const { breakpoint = 768, wideRatio = 1.2 } = options;
    
    // Default to 'small' for SSR safety
    const [shape, setShape] = useState('small');

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const updateShape = () => {
            const { innerWidth: w, innerHeight: h } = window;
            
            // Small viewport (mobile)
            if (w < breakpoint) {
                setShape('small');
                return;
            }
            
            // Big viewport - check aspect ratio
            if (w > h * wideRatio) {
                // Width is significantly greater than height (landscape desktop)
                setShape('big-wide');
            } else {
                // Height is >= width or close to square (portrait/tall desktop)
                setShape('big-tall');
            }
        };

        // Initial check
        updateShape();

        // Listen for resize events
        window.addEventListener('resize', updateShape);
        
        // Also listen for orientation changes on mobile/tablet
        window.addEventListener('orientationchange', updateShape);

        return () => {
            window.removeEventListener('resize', updateShape);
            window.removeEventListener('orientationchange', updateShape);
        };
    }, [breakpoint, wideRatio]);

    return shape;
}

/**
 * Get initial screen shape for SSR.
 * This is a synchronous function that can be called during render
 * but will only return a meaningful value on the client.
 * 
 * @returns {'big-wide' | 'big-tall' | 'small'} Current screen shape or 'small' for SSR
 */
export function getInitialScreenShape(options = {}) {
    if (typeof window === 'undefined') return 'small';
    
    const { breakpoint = 768, wideRatio = 1.2 } = options;
    const { innerWidth: w, innerHeight: h } = window;
    
    if (w < breakpoint) return 'small';
    if (w > h * wideRatio) return 'big-wide';
    return 'big-tall';
}

export default useScreenShape;
